import { useContext, useState } from 'react';
import { Check, Globe, Moon, Search, Settings2, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Context } from '@/shared/contexts/GlobalContext';
import { DocsLogo } from '@/shared/ui/DocsLogo.jsx';
import { KbdChip } from '@/shared/components/KbdChip.jsx';
import { openCommandPalette } from '@/shared/utils/commandPalette.js';
import { shortcutLabel } from '@/shared/utils/platform.js';
import { UI_LANGUAGES } from '@/shared/utils/languages.js';
import { EImzoButton } from '@/shared/ui/EImzoButton.jsx';
import { EImzoSetupModal } from '@/pages/Auth/EImzoRegistration.jsx';

// Language names are deliberately NOT translated — every entry is written in
// its own language so a user can find their language whatever the current UI
// locale is.
const FLAGS = { en: '🇺🇸', ru: '🇷🇺', uz: '🇺🇿', 'uz-Cyrl': '🇺🇿' };
const LANGUAGES = UI_LANGUAGES.map((l) => ({ code: l.code, label: l.label, flag: FLAGS[l.code] || '' }));

/**
 * Slim brand bar for the embedded (tc-mall / zehn-office) layout: logo on the
 * left, E-IMZO status + ONE settings menu (theme + language) on the right.
 * Navigation deliberately lives in the page below (the location tabs) — the
 * old "Documents" dropdown duplicated it, and its Templates entry was the
 * broken copy of the "+ New → From template…" flow.
 */
export const TopNavigation = () => {
  const { t, i18n } = useTranslation(['common', 'search']);
  const { theme, setTheme, setLang } = useContext(Context);
  const [showEImzoModal, setShowEImzoModal] = useState(false);

  const nextTheme = theme === 'light' ? 'dark' : 'light';

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLang(lng);
  };

  return (
    <div className="w-full h-11 flex items-center justify-between gap-2 px-3 sm:px-4">
      <DocsLogo />

      <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
        {/* Global search — the palette works everywhere; this teaches it. */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2"
          onClick={() => openCommandPalette()}
          aria-label={t('search:title')}
          title={t('search:everywhereHint')}
        >
          <Search className="h-4 w-4" />
          <KbdChip className="hidden sm:inline-flex">{shortcutLabel()}</KbdChip>
        </Button>

        <EImzoButton
          onClick={() => setShowEImzoModal(true)}
          className="h-8"
          labelClassName="hidden sm:inline"
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label={t('common:settings')}
            >
              <Settings2 className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('common:settings')}
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setTheme(nextTheme)}>
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              {t(`common:theme.${nextTheme}`)}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Globe className="w-3 h-3" />
              {t('common:language')}
            </DropdownMenuLabel>
            {LANGUAGES.map(({ code, label, flag }) => (
              <DropdownMenuItem key={code} onClick={() => changeLanguage(code)}>
                <span aria-hidden="true">{flag}</span>
                <span className="flex-1">{label}</span>
                {i18n.language === code && <Check className="w-4 h-4 text-brand" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* E-IMZO Modal */}
      <EImzoSetupModal open={showEImzoModal} onOpenChange={setShowEImzoModal} />
    </div>
  );
};
